import React, { useState } from 'react';
import axios from 'axios';
import { Play, Loader2, Search, Music, SkipBack, SkipForward } from 'lucide-react';
import { HymnSmartSearch } from '../../SmartSearchPanels';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const HymnPanel = ({ hymns, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHymn, setSelectedHymn] = useState(null);
  const [currentVerse, setCurrentVerse] = useState(0);
  const [language, setLanguage] = useState('english');
  const [bilingualHymn, setBilingualHymn] = useState(null);
  const [loadingHymn, setLoadingHymn] = useState(false);

  const filteredHymns = hymns.filter(h => 
    h.number?.toString().includes(searchTerm) ||
    h.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.first_line?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectHymn = async (hymn) => {
    setSelectedHymn(hymn);
    setCurrentVerse(0);
    setLoadingHymn(true);
    try {
      const res = await axios.get(`${API}/hymns/${hymn.number}/bilingual`);
      setBilingualHymn(res.data);
    } catch (error) {
      console.error('Error loading bilingual hymn:', error);
      setBilingualHymn(null);
    }
    setLoadingHymn(false);
  };

  const currentHymn = bilingualHymn?.[language] || selectedHymn;

  const handleShowHymn = async () => {
    if (!currentHymn) return;
    try {
      await axios.post(`${API}/display/show/hymn`, {
        hymn_number: currentHymn.number,
        title: currentHymn.title,
        title_english: bilingualHymn?.english?.title,
        title_yoruba: bilingualHymn?.yoruba?.title,
        verses: currentHymn.verses || [],
        current_verse: currentVerse,
        language: language
      });
      alert('Hymn displayed!');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleVerseChange = async (index) => {
    setCurrentVerse(index);
    if (currentHymn) {
      try {
        await axios.post(`${API}/display/hymn/verse/${index}`);
      } catch (error) {
        console.error('Error changing verse:', error);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Smart Hymn Search (full-lyric + bilingual fuzzy) */}
      <HymnSmartSearch />
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Hymn Display</h3>
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
          {['english', 'yoruba'].map(lang => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                language === lang 
                  ? `bg-white shadow ${lang === 'english' ? 'text-blue-600' : 'text-purple-600'}` 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {lang === 'english' ? 'English' : 'Yoruba'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by number or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
              data-testid="hymn-search-input"
            />
          </div>

          <div className="max-h-80 overflow-y-auto space-y-2 pr-2">
            {filteredHymns.slice(0, 30).map(hymn => (
              <button
                key={`${hymn.number}-${hymn.language}`}
                onClick={() => handleSelectHymn(hymn)}
                data-testid={`hymn-item-${hymn.number}`}
                className={`w-full text-left p-3 rounded-xl transition-colors ${
                  selectedHymn?.number === hymn.number
                    ? 'bg-blue-100 border-2 border-blue-500'
                    : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold text-blue-600 whitespace-nowrap">#{hymn.number}</span>
                  <span className="text-slate-700 truncate">{hymn.title}</span>
                </div>
                {hymn.first_line && (
                  <p className="text-sm text-slate-500 truncate mt-1 italic">"{hymn.first_line}"</p>
                )}
              </button>
            ))}
            {filteredHymns.length === 0 && (
              <p className="text-center text-slate-500 py-4">No hymns found</p>
            )}
            {filteredHymns.length > 30 && (
              <p className="text-center text-slate-400 text-sm py-2">
                Showing 30 of {filteredHymns.length} results...
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {loadingHymn ? (
            <div className="flex items-center justify-center h-64 bg-slate-100 rounded-xl">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : currentHymn ? (
            <>
              <div className="p-4 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl text-white" style={{ minHeight: '12rem', maxHeight: '24rem', overflow: 'auto' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-amber-400 text-sm font-medium">Hymn #{currentHymn.number}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    language === 'english' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                  }`}>
                    {language === 'english' ? 'English' : 'Yoruba'}
                  </span>
                </div>
                <h4 className="text-lg font-bold mb-3">{currentHymn.title}</h4>
                {currentHymn.verses && currentHymn.verses[currentVerse] && (
                  <p className="text-sm leading-relaxed whitespace-pre-line text-white/90" data-testid="hymn-preview-verse">
                    {currentHymn.verses[currentVerse]}
                  </p>
                )}
                {(!currentHymn.verses || currentHymn.verses.length === 0) && (
                  <p className="text-white/50 italic">No verses available for this hymn</p>
                )}
              </div>

              {currentHymn.verses && currentHymn.verses.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {currentHymn.verses.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleVerseChange(idx)}
                      data-testid={`verse-btn-${idx}`}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        currentVerse === idx
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {idx === 0 ? 'Verse 1' : idx === currentHymn.verses.length - 1 && currentHymn.chorus ? 'Chorus' : `Verse ${idx + 1}`}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={handleShowHymn}
                data-testid="display-hymn-btn"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 shadow-lg"
              >
                <Play className="w-5 h-5" />
                Display Hymn
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 bg-slate-100 rounded-xl">
              <Music className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500">Select a hymn to preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HymnPanel;
